// WellCard.js
// This is the Well Card component for the Wells tab, used to hold wellcard information for a single well in our sampling event form. This will be the area that a user inputs all sampling data
import React, { useState } from "react";
import Select from "react-select";
// Importing the useAppStore for the notification modal
// Adding the appStore
import { useAppStore } from "../../../../../store/appStore";
// Constants
import {
  PURGE_DEVICE_OPTIONS,
  SAMPLING_DEVICE_OPTIONS,
  BOTTOM_CONDITIONS_OPTIONS,
  SAMPLING_FIELD_ERROR_LABELS,
  FIELD_MEASUREMENT_ERROR_LABELS,
} from "../../../../../constants/samplingEventConstants";
// Utils
import { calculatePurgeVolume } from "../../../samplingEventUtils";
// Shared Components
import { SamplingEventDatePicker } from "../../../common/SamplingEventDatePicker";
import { SamplingEventTimePickerInput } from "../../../common/SamplingEventTimePickerInput";
import { ConfirmationModal } from "../../../common/ConfirmationModal";
// Specific components
import { FieldMeasurementSection } from "./FieldMeasurementSection";
import { SamplingInformationSection } from "./SamplingInformationSection";
import { SamplerSignaturesSection } from "./SamplerSignaturesSection";
import { QcSamplingSignatureSection } from "./QcSamplingSignatureSection";
// CSS Styles
import styles from "../WellsTab.module.css";

// Main Well Card Component
export const WellCard = ({
  well,
  isExpanded,
  onToggle,
  allEmployees,
  qaQcEmployees,
  samplingEventMode,
  actions,
  ui,
  emitPatch,
}) => {
  // UI State updates and handlers, and notification stuff
  const showNotificationModal = useAppStore(
    (s) => s.notificationActions.showNotification
  );

  // Determine if LNAPL fields should be disabled
  const lnaplFieldsDisabled = !well.lnaplPresent;

  // Determine if DNAPL fields should be disabled
  const dnaplFieldsDisabled = !well.dnaplPresent;

  // State for dealing with the error list if it is returned by an action
  const [errorList, setErrorList] = useState([]);

  // Function to get the class name for a field based on if it has an error in our errorList
  const getFieldClassName = (field) => {
    const error = errorList.find((error) => error.field === field);
    return error
      ? `${styles.samplingControl} ${styles.fieldInvalid}`
      : styles.samplingControl;
  };

  // Helper function to get the currently selected option for a field
  const getSelectedOption = (options, field) => {
    const value = well[field];
    return value ? options.find((option) => option.value === value) : null;
  };

  const handleCloseConfirmationModal = () => {
    actions.closeConfirmationModal();
  };

  // Data update functions
  // Function to handle date changes/selections
  const handleDateChange = (field, date) => {
    actions.updateWellSample(well.id, [{ field, value: date }], emitPatch);
  };

  // Helper to calculate purge volume if conditions are met
  const getCalculatedPurgeVolume = (
    depthToWaterValue = well.measuredDepthToWaterSampling,
    totalDepthValue = well.measuredTotalDepthSampling
  ) => {
    const calculatedVolume = calculatePurgeVolume(
      totalDepthValue,
      depthToWaterValue,
      well.casingDiameter
    );
    if (calculatedVolume !== null) {
      return calculatedVolume.toFixed(2);
    }
    return null;
  };

  // Function to handle checkbox changes
  const handleCheckboxChange = (field, value) => {
    // Special handling for purgeThreeWvs - calculate purge volume when checking
    if (field === "purgeThreeWvs" && value === true) {
      const purgeVolume = getCalculatedPurgeVolume();
      const updates = [{ field: "purgeThreeWvs", value: true }];
      if (purgeVolume !== null) {
        updates.push({
          field: "purgeVolumeIfPurgeThreeWvs",
          value: purgeVolume,
        });
      }
      actions.updateWellSample(well.id, updates, emitPatch);
    } else {
      actions.updateWellSample(well.id, [{ field, value }], emitPatch);
    }
  };

  // Function to handle well inspection updates from select options
  const handleSelectChange = (field, selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    actions.updateWellSample(well.id, [{ field, value }], emitPatch);
  };

  // Function to handle well inspection onChange updates for text input fields so we don't overload the server with text inputs
  const handleTextInputChange = (field, value) => {
    actions.updateWellSample(well.id, [{ field, value }]);
  };

  // Function to handle well inspection onBlur updates for text fields
  const handleTextInputOnBlur = (field, value) => {
    actions.updateWellSample(well.id, [{ field, value }], emitPatch);
  };

  // Function to handle well inspection numeric input changes
  const handleNumericInput = (field, value) => {
    // Allow empty string, digits, and one decimal point
    if (!/^[0-9]*\.?[0-9]*$/.test(value)) {
      return;
    }

    // Store the raw value during typing (no formatting)
    actions.updateWellSample(
      well.id,
      [{ field, value: value === "" ? null : value }],
      null // Don't emit patch while typing
    );
  };

  // Function to handle blur - formats value and sends patch to server
  const handleNumericBlur = (field, value) => {
    let formattedValue = null;

    if (value !== "" && value !== null && value !== undefined) {
      // Parse and format to 2 decimal places on blur
      const parsed = parseFloat(value);
      if (!isNaN(parsed)) {
        formattedValue = parsed.toFixed(2);
      }
    }

    const updates = [{ field, value: formattedValue }];

    // If this is a depth field and purgeThreeWvs is checked, recalculate purge volume
    if (
      (field === "measuredDepthToWaterSampling" ||
        field === "measuredTotalDepthSampling") &&
      well.purgeThreeWvs
    ) {
      // Use the updated value for calculation
      const depthToWater =
        field === "measuredDepthToWaterSampling"
          ? formattedValue
          : well.measuredDepthToWaterSampling;
      const totalDepthVal =
        field === "measuredTotalDepthSampling"
          ? formattedValue
          : well.measuredTotalDepthSampling;

      const purgeVolume = getCalculatedPurgeVolume(depthToWater, totalDepthVal);
      if (purgeVolume !== null) {
        updates.push({
          field: "purgeVolumeIfPurgeThreeWvs",
          value: purgeVolume,
        });
      }
    }

    // Update with formatted value and emit patch to server
    actions.updateWellSample(well.id, updates, emitPatch);
  };

  // Calculate the purge volume based on measuredDepthToWaterSampling and TotalDepthSampling.
  const depthSampling = well.measuredDepthToWaterSampling;
  const totalSampling = well.measuredTotalDepthSampling;

  const purgeVolumeDisabled =
    !depthSampling ||
    depthSampling === "" ||
    parseFloat(depthSampling) === 0.0 ||
    !totalSampling ||
    totalSampling === "" ||
    parseFloat(totalSampling) === 0.0 ||
    !well.casingDiameter ||
    well.casingDiameter === "";

  const depthToWater = parseFloat(depthSampling);
  const totalDepth = parseFloat(totalSampling);
  const invalidDepthValues =
    !isNaN(depthToWater) && !isNaN(totalDepth) && depthToWater >= totalDepth;

  const wellDataFieldsDisabled = well.purgeThreeWvs;

  // Handle auto-fill button for start date and time
  const handleAutoFillStartDateTime = () => {
    const currentDate = new Date();
    const formattedTime = currentDate.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    // Update both fields at once
    actions.updateWellSample(
      well.id,
      [
        { field: "samplingStartDate", value: currentDate },
        { field: "samplingStartTime", value: formattedTime },
      ],
      emitPatch
    );
  };

  // Handle auto-fill button for end date and time
  const handleAutoFillEndDateTime = () => {
    const currentDate = new Date();
    const formattedTime = currentDate.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    // Update both fields at once
    actions.updateWellSample(
      well.id,
      [
        { field: "samplingEndDate", value: currentDate },
        { field: "samplingEndTime", value: formattedTime },
      ],
      emitPatch
    );
  };

  // Handle confirming the well as complete
  const handleConfirmMarkComplete = () => {
    actions.updateWellSample(
      well.id,
      [
        { field: "samplingCompleted", value: true },
        { field: "samplingCompletedDate", value: new Date() },
      ],
      emitPatch
    );
    actions.closeConfirmationModal();
  };

  // Handle validating and marking the well as complete
  const handleValidateAndMarkComplete = () => {
    // Validate all required fields
    const errorList = actions.validateSamplingAndMarkComplete(
      well.id,
      SAMPLING_FIELD_ERROR_LABELS,
      FIELD_MEASUREMENT_ERROR_LABELS
    );

    if (errorList.length > 0) {
      setErrorList(errorList);
      // Get list of "labels" from errorList
      const errorLabels = errorList.map((error) => error.label).join(", ");
      // Show the notification modal and list out the errors
      showNotificationModal({
        title: "Validation Errors",
        type: "error",
        message: `Please fill out the following fields: ${errorLabels}`,
        errors: errorList,
      });
      return;
    } else {
      actions.openConfirmationModal();
    }
  };

  return (
    <div
      className={`${styles.wellCard} ${
        well.samplingCompleted ? styles.wellCardCompleted : ""
      }`}
    >
      <div className={styles.wellCardHeader} onClick={onToggle}>
        <h3>
          {well.wellId || "Unknown Well ID"}
          {well.samplingCompleted && " ✓"}
          {well.samplingCompleted &&
            well.samplingCompletedDate &&
            ` - ${well.samplingCompletedDate}`}
          {well.samplingCompleted &&
            well.employeeSignatures &&
            (() => {
              // Filter out empty/null employeeName to avoid blank initials with commas
              const initialsArray = well.employeeSignatures
                .filter(
                  (employee) =>
                    !!employee.employeeName && !!employee.employeeName.trim()
                )
                .map((employee) => {
                  const names = employee.employeeName.split(" ");
                  return names.map((name) => name[0]).join("");
                });
              return initialsArray.length > 0
                ? ` - ${initialsArray.join(", ")}`
                : "";
            })()}
        </h3>
        <span className={styles.expandIcon}>{isExpanded ? "▼" : "▶"}</span>
      </div>

      {isExpanded && (
        <div className={styles.wellCardContent}>
          {/* General Information Section */}
          <div className={styles.wellSection}>
            <h4>1. General Information</h4>

            <div className={styles.wellFieldRow}>
              <div className={styles.wellField}>
                <label htmlFor={`sampleId-${well.id}`}>Sample ID:</label>
                <input
                  type="text"
                  id={`sampleId-${well.id}`}
                  name="wellSampleId"
                  value={well.wellSampleId}
                  onChange={(e) =>
                    handleTextInputChange("wellSampleId", e.target.value)
                  }
                  onBlur={(e) =>
                    handleTextInputOnBlur("wellSampleId", e.target.value)
                  }
                  className={getFieldClassName("wellSampleId")}
                  placeholder="Sample ID"
                />
              </div>

              <div className={styles.wellField}>
                <label htmlFor={`weatherConditions-${well.id}`}>
                  Weather Conditions:
                </label>
                <input
                  type="text"
                  id={`weatherConditions-${well.id}`}
                  name="weatherConditions"
                  value={well.weatherConditions}
                  onChange={(e) =>
                    handleTextInputChange("weatherConditions", e.target.value)
                  }
                  onBlur={(e) =>
                    handleTextInputOnBlur("weatherConditions", e.target.value)
                  }
                  className={getFieldClassName("weatherConditions")}
                  placeholder="Weather Conditions"
                />
              </div>

              <div className={styles.precipitationField}>
                <div className={styles.largeCheckboxContainer}>
                  <input
                    type="checkbox"
                    id={`precipitation-${well.id}`}
                    name="precipitation"
                    checked={well.precipitation || false}
                    onChange={(e) =>
                      handleCheckboxChange("precipitation", e.target.checked)
                    }
                    className={styles.largeCheckbox}
                  />
                  <label
                    htmlFor={`precipitation-${well.id}`}
                    className={styles.largeCheckboxLabel}
                  >
                    Precipitation
                  </label>
                </div>
              </div>
            </div>

            <div className={styles.dateTimeRow}>
              <button
                className={styles.autoFillButton}
                onClick={handleAutoFillStartDateTime}
                title="Auto-fill current date and time"
              >
                AutoFill Start
              </button>

              <div className={styles.dateTimeGroup}>
                <div className={styles.dateInputWrapper}>
                  <label htmlFor={`samplingStartDate-${well.id}`}>
                    Sampling Start Date:
                  </label>
                  <SamplingEventDatePicker
                    id={`samplingStartDate-${well.id}`}
                    selected={well.samplingStartDate}
                    onChange={(date) =>
                      handleDateChange("samplingStartDate", date)
                    }
                    dateFormat="MM/dd/yyyy"
                    className={getFieldClassName("samplingStartDate")}
                    placeholderText="Start Date"
                    isClearable={true}
                    showPopperArrow={false}
                    onFocus={(e) => (e.target.readOnly = true)}
                    showMonthDropdown
                    showYearDropdown
                    scrollableYearDropdown
                  />
                </div>

                <div className={styles.timeInputWrapper}>
                  <label htmlFor={`samplingStartTime-${well.id}`}>
                    Sampling Start Time:
                  </label>
                  <SamplingEventTimePickerInput
                    value={well.samplingStartTime}
                    onChange={(time) =>
                      handleTextInputChange("samplingStartTime", time)
                    }
                    onBlur={(e) =>
                      handleTextInputOnBlur("samplingStartTime", e.target.value)
                    }
                    className={getFieldClassName("samplingStartTime")}
                  />
                </div>
              </div>
            </div>

            <div className={styles.dateTimeRow}>
              <button
                className={styles.autoFillButton}
                onClick={handleAutoFillEndDateTime}
                title="Auto-fill current date and time"
              >
                AutoFill End
              </button>
              <div className={styles.dateTimeGroup}>
                <div className={styles.dateInputWrapper}>
                  <label htmlFor={`samplingEndDate-${well.id}`}>
                    Sampling End Date:
                  </label>
                  <SamplingEventDatePicker
                    id={`samplingEndDate-${well.id}`}
                    selected={well.samplingEndDate}
                    onChange={(date) =>
                      handleDateChange("samplingEndDate", date)
                    }
                    dateFormat="MM/dd/yyyy"
                    className={getFieldClassName("samplingEndDate")}
                    placeholderText="End Date"
                    isClearable={true}
                    showPopperArrow={false}
                    onFocus={(e) => (e.target.readOnly = true)}
                    showMonthDropdown
                    showYearDropdown
                    scrollableYearDropdown
                  />
                </div>

                <div className={styles.timeInputWrapper}>
                  <label htmlFor={`samplingEndTime-${well.id}`}>
                    Sampling End Time:
                  </label>
                  <SamplingEventTimePickerInput
                    value={well.samplingEndTime}
                    onChange={(time) =>
                      handleTextInputChange("samplingEndTime", time)
                    }
                    onBlur={(e) =>
                      handleTextInputOnBlur("samplingEndTime", e.target.value)
                    }
                    className={getFieldClassName("samplingEndTime")}
                  />
                </div>
              </div>
            </div>

            <div className={styles.measurementsRow}>
              <div className={styles.measurementField}>
                <label htmlFor={`sampleTargetDepth-${well.id}`}>
                  Target Sample Depth (ft):
                </label>
                <input
                  type="text"
                  id={`sampleTargetDepth-${well.id}`}
                  name="sampleTargetDepth"
                  value={well.sampleTargetDepth}
                  onChange={(e) =>
                    handleNumericInput("sampleTargetDepth", e.target.value)
                  }
                  onBlur={(e) =>
                    handleNumericBlur("sampleTargetDepth", e.target.value)
                  }
                  className={getFieldClassName("sampleTargetDepth")}
                  placeholder="0.00"
                  disabled={well.sampleTargetDepthNm}
                />
              </div>
              <div className={styles.measurementField}>
                <label htmlFor={`sampleTargetDepthNm-${well.id}`}>
                  Target Depth N/A:
                </label>
                <input
                  type="checkbox"
                  id={`sampleTargetDepthNm-${well.id}`}
                  name="sampleTargetDepthNm"
                  checked={well.sampleTargetDepthNm || false}
                  onChange={(e) =>
                    handleCheckboxChange(
                      "sampleTargetDepthNm",
                      e.target.checked
                    )
                  }
                  className={styles.largeCheckbox}
                />
              </div>

              <div className={styles.measurementField}>
                <label htmlFor={`extractionRate-${well.id}`}>
                  Extraction Rate:
                </label>
                <input
                  type="text"
                  id={`extractionRate-${well.id}`}
                  name="extractionRate"
                  value={well.extractionRate}
                  onChange={(e) =>
                    handleNumericInput("extractionRate", e.target.value)
                  }
                  onBlur={(e) =>
                    handleNumericBlur("extractionRate", e.target.value)
                  }
                  className={getFieldClassName("extractionRate")}
                  placeholder="0.00"
                  disabled={well.extractionRateNm}
                />
              </div>
              <div className={styles.measurementField}>
                <label htmlFor={`extractionRateNm-${well.id}`}>
                  Extraction Rate N/A:
                </label>
                <input
                  type="checkbox"
                  id={`extractionRateNm-${well.id}`}
                  name="extractionRateNm"
                  checked={well.extractionRateNm || false}
                  onChange={(e) =>
                    handleCheckboxChange("extractionRateNm", e.target.checked)
                  }
                  className={styles.largeCheckbox}
                />
              </div>

              <div className={styles.measurementField}>
                <label htmlFor={`volumePurged-${well.id}`}>
                  Volume Purged:
                </label>
                <input
                  type="text"
                  id={`volumePurged-${well.id}`}
                  name="volumePurged"
                  value={well.volumePurged}
                  onChange={(e) =>
                    handleNumericInput("volumePurged", e.target.value)
                  }
                  onBlur={(e) =>
                    handleNumericBlur("volumePurged", e.target.value)
                  }
                  className={getFieldClassName("volumePurged")}
                  placeholder="0.00"
                />
              </div>
            </div>
            <div className={styles.measurementsRow}>
              <div className={styles.measurementField}>
                <label htmlFor={`purgeDevice-${well.id}`}>Purge Device:</label>
                <Select
                  id={`purgeDevice-${well.id}`}
                  value={getSelectedOption(PURGE_DEVICE_OPTIONS, "purgeDevice")}
                  onChange={(selected) =>
                    handleSelectChange("purgeDevice", selected)
                  }
                  options={PURGE_DEVICE_OPTIONS}
                  className={`${getFieldClassName("purgeDevice")} ${
                    styles.selectControl
                  }`}
                  placeholder="Select"
                  isClearable
                  classNamePrefix="select"
                />
              </div>
              {well.purgeDevice === "Other" && (
                <div className={styles.measurementField}>
                  <label htmlFor={`purgeDeviceOther-${well.id}`}>
                    Purge Device Other:
                  </label>
                  <input
                    type="text"
                    id={`purgeDeviceOther-${well.id}`}
                    name="purgeDeviceOther"
                    value={well.purgeDeviceOther}
                    onChange={(e) =>
                      handleTextInputChange("purgeDeviceOther", e.target.value)
                    }
                    onBlur={(e) =>
                      handleTextInputOnBlur("purgeDeviceOther", e.target.value)
                    }
                    className={getFieldClassName("purgeDeviceOther")}
                    placeholder="Other"
                  />
                </div>
              )}
            </div>
            <div className={styles.measurementsRow}>
              <div className={styles.measurementField}>
                <label htmlFor={`samplingDevice-${well.id}`}>
                  Sampling Device:
                </label>
                <Select
                  id={`samplingDevice-${well.id}`}
                  value={getSelectedOption(
                    SAMPLING_DEVICE_OPTIONS,
                    "samplingDevice"
                  )}
                  onChange={(selected) =>
                    handleSelectChange("samplingDevice", selected)
                  }
                  options={SAMPLING_DEVICE_OPTIONS}
                  className={`${getFieldClassName("samplingDevice")} ${
                    styles.selectControl
                  }`}
                  placeholder="Select"
                  isClearable
                  classNamePrefix="select"
                />
              </div>
              {well.samplingDevice === "Other" && (
                <div className={styles.measurementField}>
                  <label htmlFor={`samplingDeviceOther-${well.id}`}>
                    Sampling Device Other:
                  </label>
                  <input
                    type="text"
                    id={`samplingDeviceOther-${well.id}`}
                    name="samplingDeviceOther"
                    value={well.samplingDeviceOther}
                    onChange={(e) =>
                      handleTextInputChange(
                        "samplingDeviceOther",
                        e.target.value
                      )
                    }
                    onBlur={(e) =>
                      handleTextInputOnBlur(
                        "samplingDeviceOther",
                        e.target.value
                      )
                    }
                    className={getFieldClassName("samplingDeviceOther")}
                    placeholder="Other"
                  />
                </div>
              )}
            </div>

            <div className={styles.methodsRow}>
              <div className={styles.methodColumn}>
                <label>Sampling Method:</label>
                <div className={styles.verticalCheckboxes}>
                  <div className={styles.checkboxContainer}>
                    <input
                      type="checkbox"
                      id={`lowFlow-${well.id}`}
                      name="lowFlow"
                      checked={well.lowFlow || false}
                      onChange={(e) =>
                        handleCheckboxChange("lowFlow", e.target.checked)
                      }
                    />
                    <label htmlFor={`lowFlow-${well.id}`}>Low Flow</label>
                  </div>
                  <div className={styles.checkboxContainer}>
                    <input
                      type="checkbox"
                      id={`purgeThreeWvs-${well.id}`}
                      name="purgeThreeWvs"
                      checked={well.purgeThreeWvs || false}
                      onChange={(e) =>
                        handleCheckboxChange("purgeThreeWvs", e.target.checked)
                      }
                      disabled={purgeVolumeDisabled}
                    />
                    <label htmlFor={`purgeThreeWvs-${well.id}`}>
                      Purge 3 WVs
                      {purgeVolumeDisabled && (
                        <span className={styles.helperText}>
                          (fill out Depth to GW and Measured TD fields first)
                        </span>
                      )}
                      {invalidDepthValues && (
                        <span className={styles.helperText}>
                          (Depth to GW must be less than Measured TD)
                        </span>
                      )}
                    </label>
                  </div>
                </div>
              </div>

              <div className={styles.methodColumn}>
                <label htmlFor={`purgeVolumeIfPurgeThreeWvs-${well.id}`}>
                  Purge Volume if Purging 3 WVs:
                </label>
                <input
                  type="text"
                  id={`purgeVolumeIfPurgeThreeWvs-${well.id}`}
                  name="purgeVolumeIfPurgeThreeWvs"
                  value={well.purgeVolumeIfPurgeThreeWvs || ""}
                  onChange={(e) =>
                    handleNumericInput(
                      "purgeVolumeIfPurgeThreeWvs",
                      e.target.value
                    )
                  }
                  onBlur={(e) =>
                    handleNumericBlur(
                      "purgeVolumeIfPurgeThreeWvs",
                      e.target.value
                    )
                  }
                  className={getFieldClassName("purgeVolumeIfPurgeThreeWvs")}
                  placeholder="Calculated Volume"
                  disabled={!well.purgeThreeWvs}
                  readOnly={well.purgeThreeWvs} // Make field read-only when auto-calculated
                />
              </div>

              <div className={styles.methodColumn}>
                <label>Sample Options:</label>
                <div className={styles.verticalCheckboxes}>
                  <div className={styles.checkboxContainer}>
                    <input
                      type="checkbox"
                      id={`sampleFiltered-${well.id}`}
                      name="sampleFiltered"
                      checked={well.sampleFiltered || false}
                      onChange={(e) =>
                        handleCheckboxChange("sampleFiltered", e.target.checked)
                      }
                    />
                    <label htmlFor={`sampleFiltered-${well.id}`}>
                      Sample Filtered
                    </label>
                  </div>
                  <div className={styles.checkboxContainer}>
                    <input
                      type="checkbox"
                      id={`dedicatedEquipment-${well.id}`}
                      name="dedicatedEquipment"
                      checked={well.dedicatedEquipment || false}
                      onChange={(e) =>
                        handleCheckboxChange(
                          "dedicatedEquipment",
                          e.target.checked
                        )
                      }
                    />
                    <label htmlFor={`dedicatedEquipment-${well.id}`}>
                      Dedicated Equipment
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Well Data Section */}
          <div className={styles.wellSection}>
            <h4>2. Well Data</h4>
            {wellDataFieldsDisabled && (
              <div className={styles.helperText}>
                <p>Uncheck "Purge 3 WVs" to edit depth measurements</p>
              </div>
            )}
            {invalidDepthValues && (
              <div className={styles.validationAlert}>
                <p>
                  Depth to GW must be less than Measured TD for purge volume
                  calculation
                </p>
              </div>
            )}
            <div className={styles.wellDataRow}>
              <div className={styles.wellField}>
                <label htmlFor={`depthToWater-${well.id}`}>
                  Depth to GW BTOC (ft):
                </label>
                <input
                  type="text"
                  id={`depthToWater-${well.id}`}
                  name="measuredDepthToWaterSampling"
                  value={well.measuredDepthToWaterSampling}
                  onChange={(e) =>
                    handleNumericInput(
                      "measuredDepthToWaterSampling",
                      e.target.value
                    )
                  }
                  onBlur={(e) =>
                    handleNumericBlur(
                      "measuredDepthToWaterSampling",
                      e.target.value
                    )
                  }
                  className={getFieldClassName("measuredDepthToWaterSampling")}
                  placeholder="0.00"
                  disabled={wellDataFieldsDisabled}
                />
              </div>

              <div className={styles.wellField}>
                <label htmlFor={`totalDepth-${well.id}`}>
                  Measured TD BTOC (ft):
                </label>
                <input
                  type="text"
                  id={`totalDepth-${well.id}`}
                  name="measuredTotalDepthSampling"
                  value={well.measuredTotalDepthSampling}
                  onChange={(e) =>
                    handleNumericInput(
                      "measuredTotalDepthSampling",
                      e.target.value
                    )
                  }
                  onBlur={(e) =>
                    handleNumericBlur(
                      "measuredTotalDepthSampling",
                      e.target.value
                    )
                  }
                  className={getFieldClassName("measuredTotalDepthSampling")}
                  placeholder="0.00"
                  disabled={wellDataFieldsDisabled}
                />
              </div>

              <div className={styles.wellField}>
                <label htmlFor={`samplingBottomConditions-${well.id}`}>
                  Bottom Condition:
                </label>
                <Select
                  id={`samplingBottomConditions-${well.id}`}
                  value={getSelectedOption(
                    BOTTOM_CONDITIONS_OPTIONS,
                    "samplingBottomConditions"
                  )}
                  onChange={(selected) =>
                    handleSelectChange("samplingBottomConditions", selected)
                  }
                  options={BOTTOM_CONDITIONS_OPTIONS}
                  className={`${getFieldClassName(
                    "samplingBottomConditions"
                  )} ${styles.selectControl}`}
                  placeholder="Select"
                  isClearable
                  classNamePrefix="select"
                />
              </div>
            </div>
          </div>

          {/* NAPL Section */}
          <div className={styles.wellSection}>
            <h4>3. NAPL</h4>

            {/* LNAPL Section */}
            <div className={styles.naplPresentRow}>
              <div className={styles.checkboxContainer}>
                <input
                  type="checkbox"
                  id={`lnaplPresent-${well.id}`}
                  name="lnaplPresent"
                  checked={well.lnaplPresent || false}
                  onChange={(e) =>
                    handleCheckboxChange("lnaplPresent", e.target.checked)
                  }
                  className={styles.largeCheckbox}
                />
                <label
                  htmlFor={`lnaplPresent-${well.id}`}
                  className={styles.naplCheckboxLabel}
                >
                  LNAPL Present
                </label>
              </div>
            </div>

            <div className={styles.naplDataRow}>
              <div className={styles.naplField}>
                <label htmlFor={`lnaplThickness-${well.id}`}>
                  LNAPL Thickness (ft):
                </label>
                <input
                  type="text"
                  id={`lnaplThickness-${well.id}`}
                  name="lnaplThickness"
                  value={well.lnaplThickness || ""}
                  onChange={(e) =>
                    handleNumericInput("lnaplThickness", e.target.value)
                  }
                  onBlur={(e) =>
                    handleNumericBlur("lnaplThickness", e.target.value)
                  }
                  className={getFieldClassName("lnaplThickness")}
                  placeholder="0.00"
                  disabled={lnaplFieldsDisabled}
                />
              </div>

              <div className={styles.naplField}>
                <label htmlFor={`lnaplOdor-${well.id}`}>Odor:</label>
                <input
                  type="text"
                  id={`lnaplOdor-${well.id}`}
                  name="lnaplOdor"
                  value={well.lnaplOdor}
                  onChange={(e) =>
                    handleTextInputChange("lnaplOdor", e.target.value)
                  }
                  onBlur={(e) =>
                    handleTextInputOnBlur("lnaplOdor", e.target.value)
                  }
                  className={getFieldClassName("lnaplOdor")}
                  placeholder="Odor"
                  disabled={lnaplFieldsDisabled}
                />
              </div>

              <div className={styles.naplField}>
                <label htmlFor={`lnaplColor-${well.id}`}>Color:</label>
                <input
                  type="text"
                  id={`lnaplColor-${well.id}`}
                  name="lnaplColor"
                  value={well.lnaplColor}
                  onChange={(e) =>
                    handleTextInputChange("lnaplColor", e.target.value)
                  }
                  onBlur={(e) =>
                    handleTextInputOnBlur("lnaplColor", e.target.value)
                  }
                  className={getFieldClassName("lnaplColor")}
                  placeholder="Color"
                  disabled={lnaplFieldsDisabled}
                />
              </div>

              <div className={styles.naplField}>
                <label htmlFor={`lnaplOther-${well.id}`}>Other:</label>
                <input
                  type="text"
                  id={`lnaplOther-${well.id}`}
                  name="lnaplOther"
                  value={well.lnaplOther}
                  onChange={(e) =>
                    handleTextInputChange("lnaplOther", e.target.value)
                  }
                  onBlur={(e) =>
                    handleTextInputOnBlur("lnaplOther", e.target.value)
                  }
                  className={getFieldClassName("lnaplOther")}
                  placeholder="Other observations"
                  disabled={lnaplFieldsDisabled}
                />
              </div>
            </div>

            {/* DNAPL Section */}
            <div className={styles.naplPresentRow}>
              <div className={styles.checkboxContainer}>
                <input
                  type="checkbox"
                  id={`dnaplPresent-${well.id}`}
                  name="dnaplPresent"
                  checked={well.dnaplPresent || false}
                  onChange={(e) =>
                    handleCheckboxChange("dnaplPresent", e.target.checked)
                  }
                  className={styles.largeCheckbox}
                />
                <label
                  htmlFor={`dnaplPresent-${well.id}`}
                  className={styles.naplCheckboxLabel}
                >
                  DNAPL Present
                </label>
              </div>
            </div>

            <div className={styles.naplDataRow}>
              <div className={styles.naplField}>
                <label htmlFor={`dnaplThickness-${well.id}`}>
                  DNAPL Thickness (ft):
                </label>
                <input
                  type="text"
                  id={`dnaplThickness-${well.id}`}
                  name="dnaplThickness"
                  value={well.dnaplThickness || ""}
                  onChange={(e) =>
                    handleNumericInput("dnaplThickness", e.target.value)
                  }
                  onBlur={(e) =>
                    handleNumericBlur("dnaplThickness", e.target.value)
                  }
                  className={getFieldClassName("dnaplThickness")}
                  placeholder="0.00"
                  disabled={dnaplFieldsDisabled}
                />
              </div>

              <div className={styles.naplField}>
                <label htmlFor={`dnaplOdor-${well.id}`}>Odor:</label>
                <input
                  type="text"
                  id={`dnaplOdor-${well.id}`}
                  name="dnaplOdor"
                  value={well.dnaplOdor}
                  onChange={(e) =>
                    handleTextInputChange("dnaplOdor", e.target.value)
                  }
                  onBlur={(e) =>
                    handleTextInputOnBlur("dnaplOdor", e.target.value)
                  }
                  className={getFieldClassName("dnaplOdor")}
                  placeholder="Odor"
                  disabled={dnaplFieldsDisabled}
                />
              </div>

              <div className={styles.naplField}>
                <label htmlFor={`dnaplColor-${well.id}`}>Color:</label>
                <input
                  type="text"
                  id={`dnaplColor-${well.id}`}
                  name="dnaplColor"
                  value={well.dnaplColor}
                  onChange={(e) =>
                    handleTextInputChange("dnaplColor", e.target.value)
                  }
                  onBlur={(e) =>
                    handleTextInputOnBlur("dnaplColor", e.target.value)
                  }
                  className={getFieldClassName("dnaplColor")}
                  placeholder="Color"
                  disabled={dnaplFieldsDisabled}
                />
              </div>

              <div className={styles.naplField}>
                <label htmlFor={`dnaplOther-${well.id}`}>Other:</label>
                <input
                  type="text"
                  id={`dnaplOther-${well.id}`}
                  name="dnaplOther"
                  value={well.dnaplOther}
                  onChange={(e) =>
                    handleTextInputChange("dnaplOther", e.target.value)
                  }
                  onBlur={(e) =>
                    handleTextInputOnBlur("dnaplOther", e.target.value)
                  }
                  className={getFieldClassName("dnaplOther")}
                  placeholder="Other observations"
                  disabled={dnaplFieldsDisabled}
                />
              </div>
            </div>
          </div>

          {/* Field Measurements Section */}
          <FieldMeasurementSection
            well={well}
            ui={ui}
            wellsActions={actions}
            emitPatch={emitPatch}
          />

          {/* Sample Information Section */}
          <SamplingInformationSection
            well={well}
            wellActions={actions}
            emitPatch={emitPatch}
          />

          {/* Sampler Signatures Section */}
          <SamplerSignaturesSection
            well={well}
            allEmployees={allEmployees}
            samplingEventMode={samplingEventMode}
            ui={ui}
            wellsActions={actions}
            emitPatch={emitPatch}
          />

          {/* Confirmation Modal */}
          {ui.isConfirmationModalOpen && (
            <ConfirmationModal
              title={"Mark Sampling as Complete"}
              message={`Are you sure you want to mark this sampling for well ${well.wellId} as complete?`}
              onCancel={handleCloseConfirmationModal}
              onConfirm={handleConfirmMarkComplete}
            />
          )}

          <div className={styles.actionButtons}>
            <button
              className={styles.markCompleteButton}
              onClick={handleValidateAndMarkComplete}
              disabled={samplingEventMode === "view"}
            >
              {well.samplingCompleted
                ? "Update Sampling Data"
                : "Mark Sampling Complete"}
            </button>
          </div>

          {/* QA/QC Sampling Signature Section */}
          <QcSamplingSignatureSection
            well={well}
            qaQcEmployees={qaQcEmployees}
            samplingEventMode={samplingEventMode}
            ui={ui}
            wellsActions={actions}
            emitPatch={emitPatch}
          />
        </div>
      )}
    </div>
  );
};
